import * as React from 'react'
import map from 'lodash/map'

import {createSelectorOptionAsItem} from '@frontrow/console/src/components/generic-selector'
import {type SelectorItemT} from '@frontrow/console/src/components/generic-selector'
import {ContentAreas} from '@frontrow/console/src/entities/helpers/content-area'
import {
  fetchRlStandardSets,
  type RlStandardSetT
} from '@frontrow/console/src/entities/models/rl-standard-set'
import {SelfContainedTeacherSelector} from '@frontrow/console/src/components/self-contained-teacher-selector'
import {patchTeacherById} from '@frontrow/console/src/helpers/api-teachers'

type ComponentLabelsT = {
  formLabel?: string
  loadingText: string
  unselectOption: string
}

type Props = {
  id: string
  teacherId: number
  selectedElaStandardSetId: string
  onError: (where: 'retrieving' | 'updating', a: unknown) => void
  onChangeSelection: (a?: string) => void
  labels: ComponentLabelsT
}

export function SelfContainedTeacherElaStandardSetSelector({
  id,
  teacherId,
  selectedElaStandardSetId: selectedItem,
  onError,
  labels,
  onChangeSelection
}: Props): React.ReactElement {
  const itemToSelectorOption = ({id, description}: RlStandardSetT) =>
    createSelectorOptionAsItem(id, description, false, id)

  const itemsToCollection = (standardSets: RlStandardSetT[]) =>
    [
      ...map(standardSets, itemToSelectorOption),
      createSelectorOptionAsItem(null, labels.unselectOption, false, 'no-ela-standard-set-option')
    ] as SelectorItemT<string>[]

  const {unselectOption: _unselectOption, ...subLabels} = labels

  const onChangeSelectionP = (selectedSetting: string) => {
    return setTeacherElaStandardSetId(teacherId, selectedSetting)
      .then(() => onChangeSelection(selectedSetting))
      .catch((err: unknown) => onError('updating', err))
  }

  const subProps = {
    id,
    labels: subLabels,
    fetchItems: {
      tag: 'be-fetched' as const,
      fetch: () => fetchRlStandardSets(ContentAreas.ela) as Promise<RlStandardSetT[]>
    },
    selectedItem,
    itemsToCollection,
    onChangeSelection: onChangeSelectionP,
    onFetchError: (err: unknown) => onError('retrieving', err)
  }

  return <SelfContainedTeacherSelector {...subProps} />
}

const setTeacherElaStandardSetId = (
  teacherId: number,
  elaStandardSetId?: string | null
): Promise<unknown> => patchTeacherById(teacherId, {elaStandardSetId})
